import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

/**
 * Simple Calendar Component
 * react-day-picker alternatifi olarak basit bir takvim implementasyonu
 */

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
]

function Calendar({
  className,
  mode = "single",
  selected,
  onSelect,
  disabled,
  initialFocus,
  defaultMonth,
  range, // { from: Date|null, to: Date|null } — görsel aralık vurgusu
  ...props
}) {
  const [currentDate, setCurrentDate] = React.useState(() => {
    if (defaultMonth) return new Date(defaultMonth)
    if (selected) return new Date(selected)
    return new Date()
  })

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Ayın ilk günü (0 = Pazar, 1 = Pazartesi, ...)
  const firstDayOfMonth = new Date(year, month, 1).getDay()
  // Pazartesi'den başlat (0 = Pazartesi olacak şekilde ayarla)
  const startDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

  // Aydaki gün sayısı
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  // Önceki ay
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  // Sonraki ay
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  // Gün seçimi
  const selectDay = (day) => {
    const newDate = new Date(year, month, day)

    // Disabled kontrolü
    if (disabled && typeof disabled === 'function' && disabled(newDate)) {
      return
    }

    onSelect?.(newDate)
  }

  // Seçili mi kontrolü
  const isSelected = (day) => {
    if (!selected) return false
    const selectedDate = new Date(selected)
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === month &&
      selectedDate.getFullYear() === year
    )
  }

  // Aralık içinde mi? (range prop'u verilmişse görsel bant)
  const sameDay = (a, day) =>
    a && a.getDate() === day && a.getMonth() === month && a.getFullYear() === year
  const inRange = (day) => {
    if (!range?.from || !range?.to) return false
    const d = new Date(year, month, day).getTime()
    const lo = Math.min(range.from.getTime(), range.to.getTime())
    const hi = Math.max(range.from.getTime(), range.to.getTime())
    return d > lo && d < hi
  }
  const isRangeEnd = (day) => sameDay(range?.from, day) || sameDay(range?.to, day)

  // Bugün mü kontrolü
  const isToday = (day) => {
    const today = new Date()
    return (
      today.getDate() === day &&
      today.getMonth() === month &&
      today.getFullYear() === year
    )
  }

  // Disabled mı kontrolü
  const isDisabled = (day) => {
    if (!disabled) return false
    if (typeof disabled === 'function') {
      return disabled(new Date(year, month, day))
    }
    return false
  }

  // Günleri oluştur
  const days = []

  // Boş günler (önceki aydan)
  for (let i = 0; i < startDay; i++) {
    days.push(<div key={`empty-${i}`} className="h-9 w-9" />)
  }

  // Ayın günleri
  for (let day = 1; day <= daysInMonth; day++) {
    const dayDisabled = isDisabled(day)
    days.push(
      <button
        key={day}
        type="button"
        onClick={() => selectDay(day)}
        disabled={dayDisabled}
        className={cn(
          "h-9 w-9 rounded-md text-sm font-normal",
          "hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400",
          inRange(day) && "bg-slate-200 rounded-none",
          isRangeEnd(day) && "bg-slate-900 text-white hover:bg-slate-900",
          isSelected(day) && "bg-slate-900 text-white hover:bg-slate-900",
          isToday(day) && !isSelected(day) && !isRangeEnd(day) && "bg-slate-100 text-slate-900 font-semibold ring-1 ring-slate-300",
          dayDisabled && "text-slate-300 cursor-not-allowed hover:bg-transparent"
        )}
      >
        {day}
      </button>
    )
  }

  return (
    <div className={cn("p-3", className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="font-medium">
          {MONTHS[month]} {year}
        </div>
        <button
          type="button"
          onClick={nextMonth}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
          )}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day names */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS.map((day) => (
          <div
            key={day}
            className="h-9 w-9 flex items-center justify-center text-xs text-slate-500 font-medium"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {days}
      </div>
    </div>
  )
}

Calendar.displayName = "Calendar"

export { Calendar }
